import { Menu, MenuItem } from "@mui/material";
import { track } from "@SonarCX/lib/mixpanel";
import React from "react";
import { useHistory } from "react-router";

import { useAppDispatch, useAppSelector } from "@SonarCX/hooks/redux";
import useFavorites from "@SonarCX/hooks/useFavorites";
import { useEnvironment } from "@SonarCX/providers/EnvironmentProvider";
import { useTaskAPI } from "@SonarCX/providers/TaskAPIProvider";
import { Routes } from "@SonarCX/Routes";
import { AddErrorAlert, AddSuccessAlert } from "@SonarCX/slices/alert";
import { addFavorite, removeFavorite } from "@SonarCX/slices/favorites";
import { selectTaskStatus } from "@SonarCX/slices/taskStatus";

interface MenuItemProps {
  title: string;
  action:
    | "open"
    | "close"
    | "complete"
    | "completeAndClose"
    | "favorite"
    | "share";
  taskId: string;
  actionId?: string;
}

interface TaskRowRightClickMenuProps {
  menuPosition: { xPos: number; yPos: number };
  handleClose: () => void;
  handleShare: () => void;
  task: any;
}

const TaskRowRightClickMenu: React.FC<TaskRowRightClickMenuProps> = ({
  menuPosition,
  handleClose,
  handleShare,
  task,
}) => {
  const dispatch = useAppDispatch();
  const { statuses } = useAppSelector(selectTaskStatus);
  const { updateTask } = useTaskAPI();
  const { favoriteTasks } = useFavorites();
  const { user } = useEnvironment();

  const isFavorite = React.useMemo(() => {
    if (!task.id) return false;

    return favoriteTasks.some((fav) => fav.item_id === task.id);
  }, [favoriteTasks, task]);

  const markCompleteStatuses = React.useMemo(
    () =>
      statuses.filter(
        (status) => status.base_status === "completed" && !status.system,
      ),
    [statuses],
  );

  const systemStatus = React.useMemo(
    () =>
      statuses.find(
        (status) => status.base_status === "completed" && status.system,
      ),
    [statuses],
  );

  const allCompletedStatus = React.useMemo(
    () => [...markCompleteStatuses, systemStatus].filter(Boolean),
    [markCompleteStatuses, systemStatus],
  );

  const isTaskCompleted = React.useMemo(() => {
    if (!task.status_id) return false;

    return allCompletedStatus.some((status) => status.id === task.status_id);
  }, [allCompletedStatus, task]);

  const menuOptions = React.useMemo(() => {
    const opts = [
      { title: "Open", action: "open", taskId: task.id },
      { title: "Share", action: "share", taskId: task.id },
      {
        title: isFavorite ? "Remove favorite" : "Favorite",
        action: "favorite",
        taskId: task.id,
      },
    ] as MenuItemProps[];
    if (task.sub_tasks > 0 || !!task.closed_at) {
      return opts;
    }
    if (isTaskCompleted) {
      opts.push({ title: "Close task", action: "close", taskId: task.id });
      return opts;
    }
    if (systemStatus) {
      opts.push({
        title: "Mark complete and close",
        action: "completeAndClose",
        taskId: task.id,
        actionId: systemStatus.id,
      });
    }

    markCompleteStatuses.forEach((status) => {
      opts.push({
        title: `Mark complete (${status.name}) and close`,
        action: "completeAndClose",
        taskId: task.id,
        actionId: status.id,
      });
    });

    return opts;
  }, [task, isFavorite, markCompleteStatuses, systemStatus, isTaskCompleted]);

  const history = useHistory();

  const doComplete = React.useCallback(
    async (taskId, statusId) => {
      try {
        await updateTask(taskId, {
          ...task,
          status_id: statusId,
        });
        dispatch(AddSuccessAlert("Task completed"));
      } catch (err) {
        dispatch(AddErrorAlert(err));
      }
    },
    [updateTask, task, dispatch],
  );

  const doClose = React.useCallback(
    async (taskId) => {
      try {
        await updateTask(taskId, {
          ...task,
          closed_at: new Date().toISOString(),
        });
        dispatch(AddSuccessAlert("Task closed"));
      } catch (err) {
        dispatch(AddErrorAlert(err));
      }
    },
    [updateTask, task, dispatch],
  );

  const doCompleteAndClose = React.useCallback(
    async (taskId, statusId) => {
      try {
        await updateTask(taskId, {
          ...task,
          status_id: statusId,
          closed_at: new Date().toISOString(),
        });
        dispatch(AddSuccessAlert("Task completed and closed"));
      } catch (err) {
        dispatch(AddErrorAlert(err));
      }
    },
    [dispatch, task, updateTask],
  );

  const doFavorite = React.useCallback(
    async (id) => {
      if (!isFavorite) {
        await dispatch(addFavorite(user.id, { item_id: id, type: "task" }));
      } else {
        await dispatch(removeFavorite(user.id, id));
      }
    },
    [isFavorite, dispatch, user],
  );

  const handleOnClick = async (option: MenuItemProps) => {
    if (option.action === "open") {
      track("TABLE_RIGHT_CLICK_OPEN_TASK");
      history.push(`${Routes.TASKS}/${option.taskId}`);
    }
    if (option.action === "complete") {
      track("TABLE_RIGHT_CLICK_COMPLETE_TASK");
      handleClose();
      await doComplete(option.taskId, option.actionId);
    }
    if (option.action === "completeAndClose") {
      track("TABLE_RIGHT_CLICK_COMPLETE_AND_CLOSE_TASK");
      handleClose();
      await doCompleteAndClose(option.taskId, option.actionId);
    }
    if (option.action === "close") {
      handleClose();
      track("TABLE_RIGHT_CLICK_CLOSE_TASK");
      await doClose(option.taskId);
    }
    if (option.action === "share") {
      handleShare();
      track("TABLE_RIGHT_CLICK_SHARE_TASK");
      handleClose();
    }
    if (option.action === "favorite") {
      handleClose();
      track("TABLE_RIGHT_CLICK_FAVORITE_TASK");
      await doFavorite(option.taskId);
    }
  };

  return (
    <>
      <Menu
        open
        anchorPosition={{ top: menuPosition.yPos, left: menuPosition.xPos }}
        anchorReference="anchorPosition"
        onClose={handleClose}
      >
        {menuOptions.map((option, index) => (
          <MenuItem
            key={`${option.title}-${index}`}
            onClick={() => handleOnClick(option)}
          >
            {option.title}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default TaskRowRightClickMenu;
